import { useState, useEffect } from "react";
import { NavShell } from "@/components/NavShell";
import { Clock, Trash2, Copy, Check, Search, FileText, Mail, TrendingUp, Sparkles, ShoppingBag } from "lucide-react";

const TEMPLATE_LABELS = {
  blog_post: "Blog Post",
  social_media: "Social Media",
  marketing_copy: "Marketing Copy",
  email: "Email",
  product_description: "Product Description",
  landing_page: "Landing Page",
};

export default function HistoryPage({ user, apiClient, onLogout }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [copiedId, setCopiedId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await apiClient.get("/history");
      setHistory(res.data.history);
    } catch {} finally {
      setLoading(false);
    }
  };

  const deleteItem = async (id) => {
    try {
      await apiClient.delete(`/history/${id}`);
      setHistory(h => h.filter(item => item.id !== id));
    } catch {}
  };

  const copyContent = async (id, content) => {
    try {
      await navigator.clipboard.writeText(content);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = content;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filtered = history.filter(item => {
    const matchSearch = !search || item.topic.toLowerCase().includes(search.toLowerCase()) || item.content.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || item.template === filter;
    return matchSearch && matchFilter;
  });

  return (
    <NavShell user={user} onLogout={onLogout} active="history">
      <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-6" data-testid="history-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>
            <span className="brand-gradient-text">Content History</span>
          </h1>
          <p className="text-gray-500">All your generated content in one place.</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search content..."
              className="w-full bg-black/20 border border-white/10 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 rounded-lg h-10 pl-10 pr-4 text-sm text-white placeholder:text-gray-500 outline-none transition-colors duration-200"
              data-testid="history-search"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {["all", ...Object.keys(TEMPLATE_LABELS)].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors duration-200 ${filter === f ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30' : 'bg-gray-900/50 text-gray-400 border border-white/5 hover:border-white/10'}`}
                data-testid={`filter-${f}`}
              >
                {f === "all" ? "All" : TEMPLATE_LABELS[f]}
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <div className="text-sm text-gray-500">{filtered.length} item{filtered.length !== 1 ? "s" : ""}</div>

        {/* Content list */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-gray-900/30 border border-white/5 rounded-xl" data-testid="history-empty">
            <Clock className="w-10 h-10 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>
              {history.length === 0 ? "No content yet" : "No results found"}
            </h3>
            <p className="text-sm text-gray-500">
              {history.length === 0 ? "Start generating to see your history here." : "Try adjusting your search or filters."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((item) => (
              <div
                key={item.id}
                className="bg-gray-900/50 border border-white/5 rounded-xl overflow-hidden hover:border-violet-500/20 transition-colors duration-300"
                data-testid={`history-item-${item.id}`}
              >
                <div className="p-4 cursor-pointer" onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium tracking-wide uppercase text-violet-400">
                          {TEMPLATE_LABELS[item.template] || item.template}
                        </span>
                        <span className="text-xs text-gray-600">|</span>
                        <span className="text-xs text-gray-500 capitalize">{item.tone}</span>
                      </div>
                      <h3 className="text-sm font-medium truncate">{item.topic}</h3>
                      {expandedId !== item.id && (
                        <p className="text-sm text-gray-400 line-clamp-2 mt-1">{item.content}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-gray-500">{new Date(item.created_at).toLocaleDateString()}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyContent(item.id, item.content); }}
                        className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-violet-400 transition-colors duration-200"
                        data-testid={`copy-${item.id}`}
                      >
                        {copiedId === item.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteItem(item.id); }}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors duration-200"
                        data-testid={`delete-${item.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
                {expandedId === item.id && (
                  <div className="px-4 pb-4 border-t border-white/5 pt-4 animate-fade-in-up">
                    <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-300" data-testid={`content-${item.id}`}>
                      {item.content}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </NavShell>
  );
}
